import type { NavigationProps, SubmenuItems } from "./NavigationProps";
import { IconChevronDown } from "@hawkins/assets";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useAnimate,
  useMotionValue,
} from "motion/react";
import { ThemeContextProvider } from "../../providers/ThemeContextProvider";
import cx from "classnames";
import { useKeyboardEvent } from "@react-hookz/web";
import { animationCurves } from "../../../utils/animationCurves";
import { Link } from "../core/Link";
import { useIsDesktop } from "../../../hooks/useIsDesktop";
import { useLocation } from "react-router";
import { ClosingShade } from "./ClosingShade";
import { NetflixAdsLogo } from "../NetflixAdsLogo";
import { Button } from "../core/Button";
import { useLenis } from "lenis/react";
import { useGetPathnameMinusLocale } from "../../../hooks/useGetPathnameMinusLocale";
import { useImagePreload } from "../../../hooks/useImagePreload";

export const DesktopNavigation = ({ root }: NavigationProps) => {
  const lenis = useLenis();
  const pathname = useGetPathnameMinusLocale();

  const isDesktop = useIsDesktop();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const rootMenuContainerRef = useRef<HTMLDivElement>(null);
  const rootMenuBaseRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  useEffect(() => {
    setOpenIndex(null);
  }, [location]);

  const isOpen = openIndex != null;

  const onClose = useCallback(() => setOpenIndex(null), []);
  const tabpanelId = useId();

  const children = root?.childrenCollection?.items ?? [];
  const homepageLink = children[0];
  const [ctaLink] = children.slice(-1);
  const baseMenuItems = children.slice(1, -1);
  const [, animate] = useAnimate();
  const boundingBoxWidthMv = useMotionValue(0);
  const boundingBoxHeightMv = useMotionValue(0);
  const boundingBoxOpacityMv = useMotionValue(0);

  useEffect(() => {
    const rootMenuContainer = rootMenuContainerRef.current,
      rootMenuBase = rootMenuBaseRef.current;
    if (!root || !rootMenuContainer || !rootMenuBase) return;
    const controller = new AbortController();
    const signal = controller.signal;

    const updateContainerSize = () => {
      if (!signal.aborted) {
        animate(
          boundingBoxHeightMv,
          (isOpen ? rootMenuContainer : rootMenuBase).clientHeight,
          animationCurves.gentleSpring
        );
        animate(
          boundingBoxWidthMv,
          (isOpen ? rootMenuContainer : rootMenuBase).clientWidth,
          animationCurves.gentleSpring
        );
        if (boundingBoxOpacityMv.get() === 0)
          animate(boundingBoxOpacityMv, 1, animationCurves.default);
      }
    };

    updateContainerSize();

    window.addEventListener("resize", updateContainerSize, { signal });

    return () => controller.abort();
  }, [
    isOpen,
    root,
    openIndex,
    isDesktop,
    animate,
    boundingBoxHeightMv,
    boundingBoxWidthMv,
    boundingBoxOpacityMv,
  ]);

  return (
    <ThemeContextProvider
      theme={{
        palette: "red",
        background: "neutral-light",
        foreground: "neutral-light",
      }}
      className="bg-palette-current-background text-palette-current-foreground"
    >
      <div className="fixed w-full top-0 py-3 container-base flex justify-between z-50">
        {homepageLink?.__typename === "Page" && (
          <ThemeContextProvider
            className="flex justify-center items-center w-28 h-10"
            theme={{ palette: "red", foreground: "true", background: "dark" }}
          >
            <Link
              href={`/${homepageLink.slug ?? ""}`}
              aria-label={homepageLink.navigationAccessibilityLabel ?? ""}
              className="transition-colors"
              onClick={() => {
                if (pathname === "/") {
                  lenis?.scrollTo(0);
                }
              }}
            >
              <NetflixAdsLogo
                className="text-palette-current-foreground w-full"
                secondarySectionProps={{
                  className: "text-brand-cream",
                }}
              />
            </Link>
          </ThemeContextProvider>
        )}
        <div
          className="relative flex flex-col items-center"
          ref={rootMenuContainerRef}
        >
          <div
            ref={rootMenuBaseRef}
            className={cx(
              "py-3 px-6 overflow-hidden hover:text-palette-current-background group peer z-50",
              isOpen && "!text-neutral-dark"
            )}
          >
            <div className="flex gap-6 text-meta">
              {baseMenuItems.map((child, index) => {
                if (!child) return null;
                if (child.__typename === "NavigationItem") {
                  return (
                    <Button
                      key={child?.title ?? index}
                      onClick={() =>
                        setOpenIndex((current) =>
                          current === index ? null : index
                        )
                      }
                      aria-expanded={index === openIndex}
                      aria-controls={tabpanelId}
                      id={`${tabpanelId}-controller-${index}`}
                      className="group/button"
                      variant="text"
                      size="inherit"
                    >
                      <span className="inline-flex gap-2 items-center transition-colors group-hover:text-neutral-dark group-aria-expanded/button:text-palette-current-true group-hover/button:!text-palette-current-true">
                        {child?.title ?? ""}
                        <IconChevronDown
                          height={12}
                          width={12}
                          className="transition-transform group-aria-expanded/button:rotate-180"
                        />
                      </span>
                    </Button>
                  );
                } else if (child.__typename === "Page") {
                  return (
                    <Button
                      key={child?.title ?? index}
                      variant="text"
                      size="inherit"
                      asChild
                    >
                      <Link
                        href={`/${child.slug}`}
                        className="group-hover:text-neutral-dark hover:!text-palette-current-true transition-all"
                      >
                        {child.navigationAlternateLabel || child.title || ""}
                      </Link>
                    </Button>
                  );
                } else return null;
              })}
            </div>
          </div>
          <DesktopMenu
            id={tabpanelId}
            labelledBy={`${tabpanelId}-controller-${openIndex}`}
            open={isOpen}
            onClose={onClose}
            items={
              typeof openIndex === "number" &&
              baseMenuItems[openIndex]?.__typename === "NavigationItem"
                ? baseMenuItems[openIndex]?.childrenCollection?.items.filter(
                    (item) => item?.__typename === "Page"
                  ) ?? null
                : null
            }
          />
          {/* This element is the background color of the menu and the part that animates its growth. */}
          <motion.div
            style={{
              height: boundingBoxHeightMv,
              width: boundingBoxWidthMv,
              opacity: boundingBoxOpacityMv,
            }}
            className={cx(
              "absolute -z-10 mx-auto peer-hover:*:bg-brand-cream *:bg-palette-current-background/20 backdrop-blur rounded-xl opacity-0",
              isOpen && "*:!bg-brand-cream"
            )}
          >
            {/*
             * The background color is a nested element so that we can animate the change in color
             * without the custom color transition timings messing with the animate function.
             */}
            <div className="size-full rounded-xl transition-colors" />
          </motion.div>
          <ClosingShade isOpen={isOpen} onClose={onClose} />
        </div>
        {ctaLink?.__typename === "Page" && (
          <div className="z-10">
            <Button
              asChild
              variant="light-on-true"
              size="default"
              className="hover:scale-110 transition-transform active:scale-90"
            >
              <Link href={`/${ctaLink.slug ?? ""}`}>
                {ctaLink.navigationAlternateLabel || ctaLink.title || ""}
              </Link>
            </Button>
          </div>
        )}
      </div>
    </ThemeContextProvider>
  );
};

const DesktopMenu = ({
  id,
  labelledBy,
  open,
  onClose,
  items = [],
}: {
  id: string;
  labelledBy: string;
  open: boolean;
  onClose: () => void;
  items: SubmenuItems | null;
}) => {
  useKeyboardEvent("Escape", onClose, []);
  useImagePreload(
    () =>
      (items ?? [])?.map((item) => item.navigationImage?.url).filter(Boolean),
    [items]
  );

  return (
    <ThemeContextProvider
      theme={{
        palette: "red",
        background: "neutral-light",
        foreground: "neutral-dark",
      }}
      className="text-palette-current-foreground"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            role="dialog"
            id={id}
            initial={{ scale: 0, opacity: 0, y: -150 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0, opacity: 0, y: -150 }}
            transition={{ duration: 0.25 }}
            className="pt-20 pb-6 px-8 flex items-center justify-center gap-4"
            aria-labelledby={open ? labelledBy : undefined}
          >
            {items?.map((item, index) => {
              if (
                !item ||
                !item.slug ||
                !(item.title || item.navigationAlternateLabel) ||
                !item.navigationImage?.url
              )
                return null;
              return (
                <AnimatePresence key={index} mode="wait">
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <Link
                      key={item.title}
                      href={`/${item.slug}`}
                      className="flex flex-col items-center justify-between group transition-[gap] gap-3 hover:gap-2"
                    >
                      <div className="w-52 aspect-video rounded-xl overflow-hidden group-hover:scale-95 transition-transform">
                        <img
                          src={item.navigationImage.url}
                          alt={item.navigationImage.title ?? ""}
                          className="w-52 aspect-video group-hover:scale-110 transition-transform"
                        />
                      </div>
                      <span className="text-button-large transition-colors group-hover:text-palette-current-true">
                        {item.navigationAlternateLabel || item.title}
                      </span>
                    </Link>
                  </motion.div>
                </AnimatePresence>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </ThemeContextProvider>
  );
};
